import { skipToken } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { formaters } from "vincent-utils";
import { trpc } from "../../main/components/trpc.ts";
import useOwner from "../../users/hooks/useOwner";
import RespondAnnouncesPanel from "./RespondAnnouncesPanel";
import RespondMessageCard from "./RespondMessageCard";
import RespondRecorder from "./RespondRecorder";

export default function RespondInboxPage() {
  const owner = useOwner();
  const trpcUtils = trpc.useUtils();
  trpc.subscriptions.onChange.useSubscription(
    { owner: owner as string },
    {
      enabled: !!owner,
      onData: (data) => {
        if (!data || typeof data !== "object") {
          return;
        }

        if ("collection" in data && data.collection === "respond") {
          trpcUtils.respond.getHomeForOwner.invalidate();
        }
      },
    },
  );
  const respondHomeQuery = trpc.respond.getHomeForOwner.useQuery(
    owner ? { limit: 100 } : skipToken,
  );
  const respondHome = respondHomeQuery.data;
  const [forwardingNumber, setForwardingNumber] = useState("");

  useEffect(() => {
    setForwardingNumber(respondHome?.forwardingNumber || "");
  }, [respondHome?.forwardingNumber]);

  const saveForwardingNumberMutation =
    trpc.respond.setForwardingNumberForOwner.useMutation({
      onSuccess: () => {
        trpcUtils.respond.getHomeForOwner.invalidate();
      },
    });

  const selectAnnounceSlotMutation =
    trpc.respond.selectAnnounceSlotForOwner.useMutation({
      onSuccess: () => {
        trpcUtils.respond.getHomeForOwner.invalidate();
      },
    });

  const deleteMessageMutation = trpc.respond.deleteMessageForOwner.useMutation({
    onSuccess: () => {
      trpcUtils.respond.getHomeForOwner.invalidate();
    },
  });

  const formattedForwardingNumber =
    formaters.formatTelNumberForE164(forwardingNumber) || "";
  const isForwardingTouched =
    (respondHome?.forwardingNumber || "") !== forwardingNumber;

  const announceNamesInSlots = respondHome?.announceSettings
    ? [
        respondHome.announceSettings.slot_one,
        respondHome.announceSettings.slot_two,
        respondHome.announceSettings.slot_three,
      ].map(
        (announceId) =>
          respondHome.announces.find((announce) => announce.id === announceId)
            ?.name,
      )
    : [];

  const selectedSlot = respondHome?.announceSettings?.selected_slot || null;
  const hasNoAnnounceSelected =
    !selectedSlot ||
    !!(selectedSlot && !announceNamesInSlots[selectedSlot - 1]);

  const announceSlotButtons = announceNamesInSlots.map((name, index) => {
    const slot = (index + 1) as 1 | 2 | 3;
    const slotLabel = name ? `Activer le slot ${slot}` : `Slot ${slot} vide`;
    const slotButtonClassName = classNames(
      "flex min-h-[92px] cursor-pointer flex-col items-start justify-between gap-2.5 rounded-[18px] border border-slate-300 bg-white p-[14px] text-left",
      selectedSlot === slot
        ? "border-teal-300 bg-teal-100 text-teal-950"
        : !name
          ? "cursor-default bg-slate-50 text-slate-400"
          : "text-slate-900",
    );

    return (
      <button
        type="button"
        key={`respond-slot-${slot}-${name || "empty"}`}
        className={slotButtonClassName}
        title={slotLabel}
        aria-label={slotLabel}
        disabled={!name || selectAnnounceSlotMutation.isPending}
        onClick={() => {
          selectAnnounceSlotMutation.mutate({ slot });
        }}
      >
        <span className="text-[0.82rem] font-bold opacity-75">{slot}</span>
        <span>{name || "Vide"}</span>
      </button>
    );
  });

  if (!owner) {
    return (
      <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(20,184,166,0.16),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#e2e8f0_100%)] p-7 max-md:p-[18px]">
        <div className="mb-6">
          <h1 className="m-0 text-[clamp(2rem,3vw,2.8rem)] leading-none text-slate-900">
            Respond
          </h1>
          <div className="mt-2.5 text-base text-slate-600">
            Sélectionnez d&apos;abord un docteur pour afficher l&apos;espace Respond.
          </div>
        </div>
      </div>
    );
  }

  if (respondHomeQuery.isLoading) {
    return (
      <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(20,184,166,0.16),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#e2e8f0_100%)] p-7 max-md:p-[18px]">
        <div className="mb-6">
          <h1 className="m-0 text-[clamp(2rem,3vw,2.8rem)] leading-none text-slate-900">
            Respond
          </h1>
          <div className="mt-2.5 text-base text-slate-600">
            Chargement de l&apos;espace Respond...
          </div>
        </div>
      </div>
    );
  }

  if (!respondHome) {
    return (
      <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(20,184,166,0.16),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#e2e8f0_100%)] p-7 max-md:p-[18px]">
        <div className="mb-6">
          <h1 className="m-0 text-[clamp(2rem,3vw,2.8rem)] leading-none text-slate-900">
            Respond
          </h1>
          <div className="mt-2.5 text-base text-slate-600">
            Aucun compte Respond n&apos;est encore relié à ce docteur.
          </div>
        </div>

        <div className="mb-5 rounded-[22px] bg-rose-50 px-5 py-[18px] text-rose-800 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          Reliez d&apos;abord le compte Respond dans la page utilisateurs de Cadendar
          pour retrouver ici les messages, l&apos;écoute audio et les réglages.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(20,184,166,0.16),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#e2e8f0_100%)] p-7 max-md:p-[18px]">
      <div className="mb-6">
        <h1 className="m-0 text-[clamp(2rem,3vw,2.8rem)] leading-none text-slate-900">
          Respond
        </h1>
        <div className="mt-2.5 text-base text-slate-600">
          {respondHome.respondUserFullname} · {respondHome.respondUserEmail}
        </div>
      </div>

      {!respondHome.forwardingNumber ? (
        <section className="mb-5 rounded-[22px] bg-rose-50 px-5 py-[18px] text-rose-800 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          Aucun numéro de transfert n&apos;est enregistré. Ce numéro est indispensable
          pour identifier les appels transférés vers Respond.
        </section>
      ) : null}

      <section className="mb-5 rounded-[22px] bg-white/90 p-[22px] shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="text-base font-bold text-slate-900">
          Numéro dont sont transférés les appels
        </div>
        <div className="mt-[14px] grid grid-cols-[minmax(0,1fr)_auto] gap-3 max-md:grid-cols-1">
          <input
            className="min-w-0 rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
            value={forwardingNumber}
            onChange={(event) => setForwardingNumber(event.target.value)}
            placeholder="+33612345678"
          />
          <button
            type="button"
            className="cursor-pointer rounded-[14px] border-none bg-teal-200 px-[18px] py-3 font-bold text-teal-950 disabled:cursor-default disabled:bg-slate-200 disabled:text-slate-500"
            disabled={!formattedForwardingNumber || !isForwardingTouched}
            onClick={() => {
              if (!formattedForwardingNumber) {
                return;
              }
              saveForwardingNumberMutation.mutate(formattedForwardingNumber);
            }}
          >
            {saveForwardingNumberMutation.isPending
              ? "Enregistrement..."
              : "Enregistrer"}
          </button>
        </div>
        <div className="mt-2.5 text-slate-600">
          Numéro Respond: {respondHome.respondPhoneNumber || "non renseigné"}
        </div>
      </section>

      <section className="mb-5 rounded-[22px] bg-white/90 p-[22px] shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="text-base font-bold text-slate-900">Annonce active</div>
        {!respondHome.announceSettings || hasNoAnnounceSelected ? (
          <div className="mt-3 text-orange-800">
            Aucune annonce n&apos;est encore disponible ou sélectionnée. Utilisez
            l&apos;enregistreur Respond pour préparer les annonces.
          </div>
        ) : (
          <div className="mt-[14px] grid grid-cols-3 gap-2.5 max-md:grid-cols-1">
            {announceSlotButtons}
          </div>
        )}
      </section>

      <RespondRecorder
        ownerId={owner}
        onUploaded={() => {
          trpcUtils.respond.getHomeForOwner.invalidate();
        }}
      />

      <RespondAnnouncesPanel
        ownerId={owner}
        announces={respondHome.announces}
        announceSettings={respondHome.announceSettings}
        onChanged={() => {
          trpcUtils.respond.getHomeForOwner.invalidate();
        }}
      />

      <section className="mt-7 xl:mx-auto xl:max-w-5xl">
        <div className="mb-[14px] text-[1.2rem] font-bold text-slate-900">
          Messages
        </div>

        <div className="grid gap-4">
          {respondHome.messages.map((message) => (
            <RespondMessageCard
              key={message.id}
              ownerId={owner}
              message={message}
              isDeleting={
                deleteMessageMutation.isPending &&
                deleteMessageMutation.variables === message.id
              }
              onDelete={(messageId) => {
                deleteMessageMutation.mutate(messageId);
              }}
            />
          ))}
        </div>
      </section>

      {respondHome.messages.length === 0 ? (
        <div className="mb-5 rounded-[22px] bg-white/80 p-6 text-center text-slate-600 shadow-[0_24px_80px_rgba(15,23,42,0.08)] xl:mx-auto xl:max-w-5xl">
          Pas encore de messages Respond pour ce compte.
        </div>
      ) : null}

      {respondHomeQuery.error ? (
        <div className="mb-5 rounded-[22px] bg-white/80 p-6 text-center text-red-700 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          Impossible de charger les données Respond pour ce cabinet.
        </div>
      ) : null}
    </div>
  );
}